import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { apiFetch } from '../apiClient';
import { useRememberedPeriod } from './rememberedPeriod';
import { countyLabel, shortProjectName } from './milestoneTrackerLogic';
import {
  LINE_NOTE, LINE_ORDER, MONTHS, ALL_MONTHS, NEXT_DIM, contractType, agg, baselineLabel, compareHelp, compareOption, dims, groupBy, lastClosedMonth, normalize, openMonth, periodMonths,
  ROW_ORDERS, rowCompare, runningVariance, status,
} from './revenueTrackerLogic';

// Revenue Tracker (Judy, 2026-10-29): FCST vs Actuals priced in dollars -- hours x the project's
// BillingRate in effect that month -- compared to a locked forecast. Starts with Finance's monthly
// variance (last month's crystallized forecast); quarterly and annual baselines plug into the same
// views once they're locked. Three layouts from the mockup, Tracker first.

const RESOURCES_ENABLED = process.env.REACT_APP_RESOURCES_ENABLED === 'true';

// Same palette as Milestone Tracker: text and row tints from its done / due-soon / overdue states,
// fills from its card accents (green, yellow, red) -- Judy, 2026-10-29.
const ST = {
  good: { color: 'var(--green)', fill: 'var(--green)', bg: 'var(--row-done)', icon: '▲', label: 'At or over FCST' },
  bad: { color: 'var(--danger-text)', fill: 'var(--danger-text)', bg: 'var(--row-overdue)', icon: '▼', label: 'Under FCST' },
  warn: { color: 'var(--accent-orange)', fill: 'var(--accent-orange)', bg: 'var(--row-due-soon)', icon: '▼', label: 'Slightly under FCST' },
};
const money = (n) => {
  if (n == null || !Number.isFinite(n)) return '—';
  const a = Math.abs(n) < 0.5 ? 0 : Math.abs(n);
  const s = n < 0 && a ? '−' : '';
  return a >= 1e6 ? `${s}$${(a / 1e6).toFixed(2)}M` : a >= 1e3 ? `${s}$${(a / 1e3).toFixed(1)}K` : `${s}$${Math.round(a)}`;
};
const moneyS = (n) => (n == null ? '—' : Math.abs(n) < 0.5 ? '$0' : `${n > 0 ? '+' : ''}${money(n)}`);
const hrs = (n) => Math.round(n || 0).toLocaleString('en-US');
// Milestone sign-offs belong to no team, so the team view leaves them out (Judy, 2026-11-02).
const forDim = (recs, dim) => (dim === 'team' ? recs.filter((r) => r.kind !== 'signoff') : recs);
// PS and R&D hours over (+) or under (−) FCST, e.g. "PS +12h · R&D −3h".
const signH = (n) => `${n > 0.5 ? '+' : n < -0.5 ? '−' : ''}${hrs(Math.abs(n))}h`;
// Only the stream(s) that actually varied; '' when neither did.
const hoursTxt = (a) => [['PS', a.psHVar], ['R&D', a.rdHVar]].filter(([, h]) => Math.abs(h || 0) >= 0.5).map(([l, h]) => `${l} ${signH(h)}`).join(' · ');
const pctTxt = (p) => (p == null ? '—' : `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`);

function Chip({ pct, band }) {
  if (pct == null) return <span style={{ color: 'var(--text-light)', fontSize: 12 }}>—</span>;
  const s = ST[status(pct, band)];
  return (
    <span title={`${s.label} (${band}% tolerance)`} style={{ background: s.bg, color: s.color, borderRadius: 999, padding: '1px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.icon} {pctTxt(pct)}
    </span>
  );
}
// ---- Export: PDF, email (HTML) and Excel for any section or the whole page (Judy, 2026-10-30) ----

// Prints one block on its own, using the same body.print-one / .print-target rules as Milestone
// Tracker (App.css). The timeout backstops browsers that skip afterprint when the dialog closes.
function printSection(id) {
  const el = id ? document.getElementById(id) : null;
  if (!el) { window.print(); return; }
  const cleanup = () => {
    document.body.classList.remove('print-one');
    el.classList.remove('print-target');
    window.removeEventListener('afterprint', cleanup);
  };
  el.classList.add('print-target');
  document.body.classList.add('print-one');
  window.addEventListener('afterprint', cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}

const INLINE_PROPS = [
  'color', 'background-color', 'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing',
  'text-align', 'vertical-align', 'white-space', 'padding', 'margin', 'border-top', 'border-right', 'border-bottom',
  'border-left', 'border-radius', 'display', 'flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'gap',
  'grid-template-columns', 'width', 'min-width', 'opacity', 'border-collapse',
];
const SVG_PROPS = ['fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'fill-opacity', 'stroke-opacity', 'opacity', 'font-size', 'font-weight', 'font-family'];

// Charts become PNGs: Outlook drops inline SVG. Theme colors are CSS variables, so every shape's
// computed paint is written onto it first or the image would render black.
function svgToPng(svg) {
  const box = svg.getBoundingClientRect();
  const copy = svg.cloneNode(true);
  const src = [svg, ...svg.querySelectorAll('*')];
  const dst = [copy, ...copy.querySelectorAll('*')];
  src.forEach((node, i) => {
    const cs = getComputedStyle(node);
    for (const p of SVG_PROPS) dst[i].setAttribute(p, cs.getPropertyValue(p));
  });
  copy.setAttribute('width', box.width);
  copy.setAttribute('height', box.height);
  const xml = new XMLSerializer().serializeToString(copy);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = box.width * 2;
      canvas.height = box.height * 2;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0, box.width, box.height);
      resolve(`<img src="${canvas.toDataURL('image/png')}" width="${Math.round(box.width)}" height="${Math.round(box.height)}" alt="chart">`);
    };
    img.onerror = () => resolve('');
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  });
}

// Copies a section as HTML with every computed style written inline, so it pastes into Outlook
// looking the way it does on screen. Controls marked data-noexport are dropped.
async function sectionToEmailHtml(el) {
  const copy = el.cloneNode(true);
  const src = [el, ...el.querySelectorAll('*')];
  const dst = [copy, ...copy.querySelectorAll('*')];
  const swaps = [];
  for (let i = 0; i < src.length; i++) {
    const node = src[i];
    if (node.closest('svg') && node.tagName.toLowerCase() !== 'svg') continue;
    if (node.tagName.toLowerCase() === 'svg') { swaps.push([dst[i], svgToPng(node)]); continue; }
    const cs = getComputedStyle(node);
    dst[i].setAttribute('style', INLINE_PROPS.map((p) => `${p}:${cs.getPropertyValue(p)}`).join(';'));
    dst[i].removeAttribute('class');
  }
  for (const [node, png] of swaps) {
    const holder = document.createElement('span');
    holder.innerHTML = await png;
    node.replaceWith(...holder.childNodes);
  }
  copy.querySelectorAll('[data-noexport], select, input').forEach((n) => n.remove());
  return copy.outerHTML;
}

async function copyHtml(html) {
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([html], { type: 'text/plain' }),
    })]);
  } else {
    await navigator.clipboard.writeText(html);
  }
}

// Every table in the section becomes its own sheet; a section with no table exports its text lines.
function sectionToWorkbook(el) {
  const wb = XLSX.utils.book_new();
  const tables = [...el.querySelectorAll('table')];
  tables.forEach((t, i) => XLSX.utils.book_append_sheet(wb, XLSX.utils.table_to_sheet(t, { raw: true }), `Table ${i + 1}`));
  if (!tables.length) {
    const lines = el.innerText.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => [l]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(lines), 'Summary');
  }
  return wb;
}

function ExportMenu({ label, targetId, extraExcel }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  async function run(kind) {
    setOpen(false);
    const el = document.getElementById(targetId);
    if (kind === 'pdf') return printSection(targetId);
    if (!el) return;
    if (kind === 'excel') {
      if (extraExcel) return extraExcel();
      return XLSX.writeFile(sectionToWorkbook(el), `${label.replace(/[\\/:*?"<>|]/g, '-')}.xlsx`);
    }
    try {
      await copyHtml(await sectionToEmailHtml(el));
      setNote('Copied — paste into Outlook');
    } catch {
      setNote('Copy failed — your browser blocked clipboard access');
    }
    setTimeout(() => setNote(''), 4000);
  }
  return (
    <span data-noexport style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
      {note && <span style={{ fontSize: 11, color: 'var(--text-mid)' }}>{note}</span>}
      <button type="button" className="export-btn" onClick={() => setOpen((v) => !v)} title={`Export "${label}" as PDF, email or Excel`} style={{ height: 24 }}>Export ▾</button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div className="mt-pop" style={{ minWidth: 140, padding: 5 }}>
            {[['pdf', 'PDF'], ['email', 'Email (HTML)'], ['excel', 'Excel']].map(([kind, text]) => (
              <button key={kind} type="button" onClick={() => run(kind)}
                style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '5px 8px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dark)' }}>
                {text}
              </button>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

const varColor = (pct, band) => ST[status(pct, band)].color;
const varFill = (pct, band) => ST[status(pct, band)].fill;

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const summary = selected.length === 0 ? `All ${label.toLowerCase()}` : selected.length === 1 ? options.find((o) => o.id === selected[0])?.name || '1 selected' : `${selected.length} ${label.toLowerCase()}`;
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="mt-ctl" onClick={() => setOpen((v) => !v)} style={selected.length ? { borderColor: 'var(--heading)', fontWeight: 600 } : undefined}>{summary} ▾</button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setOpen(false)} />
          <div className="mt-pop" style={{ left: 0, right: 'auto' }}>
            {options.map((o) => (
              <label key={o.id} className="mt-check" style={{ display: 'flex', height: 'auto', padding: '3px 0' }}>
                <input type="checkbox" checked={selected.includes(o.id)} onChange={() => onChange(selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id])} />
                {o.name}
              </label>
            ))}
            <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px', marginTop: 6 }} onClick={() => onChange([])}>Clear</button>
          </div>
        </>
      )}
    </div>
  );
}

function niceScale(lo, hi) {
  const span = Math.max(1, hi - lo);
  const raw = span / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((x) => x * p).find((x) => x >= raw);
  return { min: Math.floor(lo / step) * step, max: Math.ceil(hi / step) * step, step };
}
function ticks({ min, max, step }) {
  const out = [];
  for (let v = min; v <= max + step / 1000; v += step) out.push(v);
  return out;
}

// Ring = ACT as a share of FCST for the month; the tick is how far through the month we are.
function MonthGauge({ pct, dayPct, color, size: shownSize = 150 }) {
  const size = 150, r = 58, c = size / 2, circ = 2 * Math.PI * r;
  const shown = Math.min(100, Math.max(0, pct ?? 0));
  const ang = dayPct == null ? null : (dayPct / 100) * 2 * Math.PI - Math.PI / 2;
  const at = (rad) => [c + rad * Math.cos(ang), c + rad * Math.sin(ang)];
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={shownSize} height={shownSize} style={{ flexShrink: 0 }} role="img" aria-label={pct == null ? 'No forecast this month' : `${Math.round(pct)}% of forecast revenue`}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--light-bg)" strokeWidth="14" />
      {pct != null && <circle cx={c} cy={c} r={r} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(shown / 100) * circ} ${circ}`} transform={`rotate(-90 ${c} ${c})`} />}
      {ang != null && <line x1={at(r - 11)[0]} y1={at(r - 11)[1]} x2={at(r + 11)[0]} y2={at(r + 11)[1]} stroke="var(--text-dark)" strokeWidth="2" strokeLinecap="round" />}
      <text x={c} y={c - 2} textAnchor="middle" fontSize="30" fontWeight="700" fill="var(--heading)">{pct == null ? '—' : `${Math.round(pct)}%`}</text>
      <text x={c} y={c + 18} textAnchor="middle" fontSize="11" fill="var(--text-mid)">{pct == null ? 'not started' : 'of FCST'}</text>
    </svg>
  );
}

// Arrow + status color for a variance: ▲ at/over, ▼ under. Without a % to judge it by (hours/rate
// effect, no FCST), it colors by sign.
function VarTxt({ v, pct, band, text, bold }) {
  if (v == null) return '—';
  if (Math.abs(v) < 0.5) return ''; // no variance reads as blank, not a green $0
  const color = pct == null ? (v >= 0 ? ST.good.color : ST.bad.color) : varColor(pct, band);
  return <span style={{ color, fontWeight: bold ? 600 : undefined, whiteSpace: 'nowrap' }}>{v >= 0 ? '▲' : '▼'} {text}</span>;
}

// Each month's variance (bars) and the running total of it from January (line).
function RevenueChart({ records, closed, band, baseShort, onMonth, selected }) {
  const W = 960, L = 60, R = 8, T = 14, H2 = 220, B = 22;
  const H = T + H2 + B;
  const data = ALL_MONTHS.map((m) => agg(records, [m]));
  const run = runningVariance(records);
  const vals = run.flatMap((r) => [r.var || 0, r.running, 0]);
  const sc = niceScale(Math.min(...vals) * 1.1, Math.max(...vals) * 1.1 || 1);
  const pw = W - L - R, gw = pw / 12, bw = Math.min(30, gw * 0.5);
  const Y = (v) => T + H2 - ((v - sc.min) / (sc.max - sc.min)) * H2;
  const X = (m) => L + gw * m + gw / 2;
  const pts = run.filter((r) => r.base > 0 || r.var);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Monthly variance and running total">
      {ticks(sc).map((v) => <g key={v}><line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} stroke="var(--border)" strokeOpacity={v === 0 ? 1 : 0.6} /><text x={L - 6} y={Y(v) + 4} textAnchor="end" fontSize={11} fill="var(--text-light)">{money(v)}</text></g>)}
      {data.map((d, m) => {
        const cx = X(m), r = run[m];
        return (
          <g key={m}>
            {selected.includes(m) && selected.length < 12 && <rect x={L + gw * m + 1} y={T} width={gw - 2} height={H2} fill="var(--hover-tint)" />}
            {r.var != null && <rect x={cx - bw / 2} y={Math.min(Y(0), Y(r.var))} width={bw} height={Math.abs(Y(0) - Y(r.var))} rx={2} fill={varFill(d.pct, band)} fillOpacity={closed.has(m) ? 0.9 : 0.45} />}
            <text x={cx} y={H - 6} textAnchor="middle" fontSize={11} fill="var(--text-light)">{MONTHS[m]}</text>
            <rect x={L + gw * m} y={T} width={gw} height={H2} fill="transparent" style={{ cursor: 'pointer' }} onClick={() => onMonth(m)}>
              <title>{`${MONTHS[m]} · ${closed.has(m) ? 'ACT' : 'FCST'} ${money(d.out)}\n${baseShort} ${money(d.base)}\nMonth variance ${moneyS(d.var)} (${pctTxt(d.pct)})\nRunning total Jan–${MONTHS[m]} ${moneyS(r.running)}`}</title>
            </rect>
          </g>
        );
      })}
      {pts.length > 0 && <polyline fill="none" stroke="var(--text-dark)" strokeWidth={2} points={pts.map((r) => `${X(r.m)},${Y(r.running)}`).join(' ')} pointerEvents="none" />}
      {pts.map((r) => <circle key={r.m} cx={X(r.m)} cy={Y(r.running)} r={3.5} fill="var(--text-dark)" stroke="var(--surface)" strokeWidth={1.5} pointerEvents="none" />)}
    </svg>
  );
}

// The same months as a table, with the YTD grand total of the variance in the last column.
function MonthTable({ records, closed, band, baseShort, plain }) {
  const data = ALL_MONTHS.map((m) => agg(records, [m]));
  const run = runningVariance(records);
  const done = ALL_MONTHS.filter((m) => closed.has(m));
  const ytd = agg(records, done);
  const last = done.length ? MONTHS[Math.max(...done)] : '';
  const head = { ...TD, fontWeight: 600, color: 'var(--text-mid)' };
  const sep = { borderLeft: `2px solid ${GROUP_LINE}` };
  return (
    <div className="sheet-wrap" style={{ marginTop: 10 }}>
      <table className="sheet">
        <thead><tr><th />{ALL_MONTHS.map((m) => <th key={m} style={{ textAlign: 'right', fontStyle: closed.has(m) ? 'normal' : 'italic' }}>{MONTHS[m]}{closed.has(m) ? '' : ' (FCST)'}</th>)}
          <th style={{ textAlign: 'right', ...sep }} title="Sum of every month with actuals">YTD total{last ? ` (Jan–${last})` : ''}</th></tr></thead>
        <tbody>
          <tr><td style={head}>ACT $</td>{data.map((d, m) => <td key={m} style={TD}>{closed.has(m) ? money(d.out) : '—'}</td>)}<td style={{ ...TD, ...sep, fontWeight: 700 }}>{money(ytd.out)}</td></tr>
          <tr><td style={head}>{baseShort} $</td>{data.map((d, m) => <td key={m} style={TD}>{money(d.base)}</td>)}<td style={{ ...TD, ...sep, fontWeight: 700 }}>{money(ytd.base)}</td></tr>
          {!plain && <><tr><td style={head}>Month var $</td>{data.map((d, m) => <td key={m} style={TD}>{d.var == null ? '—' : <VarTxt v={d.var} pct={d.pct} band={band} text={moneyS(d.var)} />}</td>)}
            <td style={{ ...TD, ...sep }}><VarTxt v={ytd.var} pct={ytd.pct} band={band} text={`${moneyS(ytd.var)} (${pctTxt(ytd.pct)})`} bold /></td></tr>
          <tr><td style={head} title="Running total of the monthly variance, Jan through that month">Running var $</td>{run.map((r) => <td key={r.m} style={TD}>{closed.has(r.m) ? <VarTxt v={r.running} band={band} text={moneyS(r.running)} /> : '—'}</td>)}
            <td style={{ ...TD, ...sep }}><VarTxt v={ytd.var} pct={ytd.pct} band={band} text={moneyS(ytd.var)} bold /></td></tr></>}
        </tbody>
      </table>
    </div>
  );
}

// [key, label, group] -- grouped so the list can draw a header band and a divider per group.
const COLS = [
  ['state', 'State', 'info'], ['county', 'County', 'info'], ['type', 'Type', 'info'], ['pm', 'PM', 'info'],
  ['outH', 'ACT hours', 'hours'], ['baseH', 'FCST hours', 'hours'], ['rate', 'Avg rate', 'hours'],
  ['ps', 'ACT $', 'ps'], ['psBase', 'FCST $', 'ps'], ['psVar', 'Var $', 'ps'],
  ['rd', 'ACT $', 'rd'], ['rdBase', 'FCST $', 'rd'], ['rdVar', 'Var $', 'rd'],
  ['out', 'Total ACT $', 'total'], ['base', 'Total FCST $', 'total'], ['var', 'Total var $', 'total'], ['pct', 'Total var %', 'total'],
  ['volVar', 'Hours effect $', 'drivers'], ['rateVar', 'Rate effect $', 'drivers'],
];
const GROUPS = { info: '', hours: 'Hours & rate', ps: 'PS', rd: 'R&D', total: 'Total (PS + R&D)', drivers: 'Why it varied' };
const GROUP_LINE = 'color-mix(in srgb, var(--primary-light) 45%, transparent)';
const INFO = ['state', 'county', 'type', 'pm'];
const DEFAULT_COLS = ['type', 'ps', 'psBase', 'psVar', 'rd', 'rdBase', 'rdVar', 'out', 'base', 'var', 'pct'];
// Compact rows so more of the list fits on screen (Judy, 2026-10-29).
const TD = { padding: '2px 8px', fontSize: 12, lineHeight: 1.3, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' };
const TDL = { ...TD, textAlign: 'left' };

function RevenueTracker() {
  const [year, setYear] = useRememberedPeriod('revenueTracker.year', null);
  const [baselineId, setBaselineId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [opt, setOpt] = useRememberedPeriod('revenueTracker.tab', 'F'); // 'F' ACT & FCST, 'A' Variance
  const [grain, setGrain] = useRememberedPeriod('revenueTracker.grain', 'month');
  const [month, setMonth] = useRememberedPeriod('revenueTracker.month', null);
  const [qtr, setQtr] = useRememberedPeriod('revenueTracker.qtr', 0);
  const [shownLines, setShownLines] = useRememberedPeriod('revenueTracker.packageLines', ['POC']);
  const [breakdown, setBreakdown] = useRememberedPeriod('revenueTracker.breakdown', 'team');
  const [afMetric, setAfMetric] = useRememberedPeriod('revenueTracker.afMetric', 'both');
  const [afBreakdown, setAfBreakdown] = useRememberedPeriod('revenueTracker.afBreakdown', 'project');
  const [rowOrder, setRowOrder] = useRememberedPeriod('revenueTracker.rowOrder', 'low');
  const [stream, setStream] = useState('all');
  const [band, setBand] = useState(2);
  const [minPct, setMinPct] = useState(0);
  const [minAbs, setMinAbs] = useState(0);
  // Opens on POC (fixed-fee) projects -- Judy, 2026-10-29.
  const [filters, setFilters] = useState({ project: [], team: [], state: [], county: [], pm: [] });
  const [view, setView] = useState('list');
  const [groupKey, setGroupKey] = useState('project');
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [colsOpen, setColsOpen] = useState(false);
  const orderSort = (o) => (o === 'alpha' ? { key: 'name', dir: 'asc' } : { key: 'var', dir: o === 'high' ? 'desc' : 'asc' });
  const [sort, setSort] = useState(() => orderSort(rowOrder));
  const pickOrder = (o) => { setRowOrder(o); setSort(orderSort(o)); };
  const [open, setOpen] = useState(() => new Set());
  const [hmDim, setHmDim] = useState('project');
  const [drill, setDrill] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tick, setTick] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    if (!RESOURCES_ENABLED) return;
    const q = new URLSearchParams();
    if (year) q.set('year', year);
    if (baselineId) q.set('baselineId', baselineId);
    setLoading(true);
    setError('');
    apiFetch(`/dashboard/reports/revenue?${q}`)
      .then((d) => {
        setData(d);
        setUpdatedAt(new Date());
        if (!year) setYear(d.year);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [year, baselineId, tick, setYear]);

  // Hours come straight from timesheets on every load; this also re-pulls them every 15 minutes
  // while the page stays open, so nobody has to refresh (Judy, 2026-10-29).
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // The month in progress shows at FCST unless "Hours logged so far" is on; then it counts the hours
  // logged to date, on both tabs (Judy, 2026-11-02).
  const [live, setLive] = useRememberedPeriod('revenueTracker.liveMonth', false);
  const toDate = !!data && live;
  const norm = useMemo(() => (data ? normalize(data, { actualsToDate: toDate }) : null), [data, toDate]);
  const current = data ? openMonth(data) : null;
  const D = useMemo(() => (norm ? dims(norm) : null), [norm]);
  const reportMonth = norm ? lastClosedMonth(norm) : null;
  const focusMonth = month ?? reportMonth ?? 0;
  const months = periodMonths(grain, focusMonth, qtr);
  const filtered = useMemo(() => {
    if (!norm) return [];
    return norm.records.filter((r) => (stream === 'all' || r.stream === stream)
      && Object.entries(filters).every(([k, sel]) => !sel.length || sel.includes(D[k].key(r))));
  }, [norm, D, stream, filters]);
  // The package lines picked at the top drive every number on the page (Judy, 2026-11-02).
  const records = useMemo(() => filtered.filter((r) => shownLines.includes(r.line)), [filtered, shownLines]);

  if (!RESOURCES_ENABLED) {
    return <div className="placeholder-page"><div className="placeholder-title">Revenue Tracker</div><div className="placeholder-sub">Set <code>REACT_APP_RESOURCES_ENABLED=true</code> to enable it.</div></div>;
  }
  if (error) return <div className="notice notice-error">⚠ {error}</div>;
  if (!norm || (loading && !data)) return <div className="card" style={{ color: 'var(--text-light)' }}>Loading revenue…</div>;

  const baseShort = norm.baseline ? `FCST (${baselineLabel(norm.baseline)})` : 'FCST';
  const periodLabel = grain === 'month' ? `${MONTHS[focusMonth]} ${norm.year}` : grain === 'qtr' ? `Q${qtr + 1} ${norm.year} total` : `${norm.year} full-year total`;
  // Spelled out because Mo / Qtr / Yr were read as "as of" rather than sums (Judy, 2026-10-30).
  const periodHelp = grain === 'month'
    ? `${MONTHS[focusMonth]} ${norm.year} only — that one month, not a running total.`
    : grain === 'qtr'
      ? `Q${qtr + 1} ${norm.year} total — ${MONTHS[qtr * 3]} + ${MONTHS[qtr * 3 + 1]} + ${MONTHS[qtr * 3 + 2]} added together. Months with actuals count at ACT, the rest at FCST.`
      : `${norm.year} full-year total — Jan through Dec added together. Months with actuals count at ACT, the rest at FCST.`;
  const optionsFor = (k) => [...new Set(norm.records.map((r) => D[k].key(r)))].map((id) => ({ id, name: D[k].name(id) })).sort((a, b) => a.name.localeCompare(b.name));
  const openDrill = (title, recs, dimKey, dMonths, dLabel) => setDrill({ title, recs, dimKey, months: dMonths, label: dLabel });

  // ---- cards ----
  // Cards follow the month picked at the top; the Month / Quarter / Year buttons only drive the list and bridge.
  const cardMonth = focusMonth;
  const mo = agg(records, [cardMonth]);
  const fy = agg(records, ALL_MONTHS);
  const ytdMonths = ALL_MONTHS.filter((m) => m <= cardMonth);
  const ytd = agg(records, ytdMonths);
  // A chart bar or a Total-row month focuses the page (and the list) on that month; again goes back to the year.
  const focusOn = (m) => { if (grain === 'month' && focusMonth === m) { setGrain('year'); setMonth(null); } else { setGrain('month'); setMonth(m); } };
  const inProgress = cardMonth === current;
  const dayPct = inProgress ? Math.round((new Date().getDate() / new Date(norm.year, cardMonth + 1, 0).getDate()) * 100) : null;
  const toDateTxt = inProgress ? ' to date' : '';
  const cards = [
    { id: 'rt-card-month', label: `Month variance · ${MONTHS[cardMonth]} ${norm.year}`, a: mo, dayPct,
      sub: `${MONTHS[cardMonth]} only: revenue ACT${toDateTxt} vs that month's FCST. Not cumulative.`,
      actL: `${MONTHS[cardMonth]} ACT${toDateTxt}`, fcstL: `${MONTHS[cardMonth]} FCST`, accent: 'accent-blue',
      dm: [cardMonth], dl: `${MONTHS[cardMonth]} ${norm.year}` },
    // FY vs a locked baseline only -- against the live FCST the full year is just the running total.
    ...(norm.baseline ? [{ id: 'rt-card-fy', label: `Full-year outlook · ${norm.year}`, a: fy,
      sub: `Jan–Dec total (ACT for closed months + FCST for the rest), now vs ${baselineLabel(norm.baseline)}.`,
      actL: 'Full year now', fcstL: 'Full year at lock', accent: 'accent-light-blue', dm: ALL_MONTHS, dl: `${norm.year} full year` }] : []),
    { id: 'rt-card-ytd', label: `Year-to-date variance · Jan–${MONTHS[cardMonth]} ${norm.year}`, a: ytd,
      sub: `Running total: revenue ACT${toDateTxt} vs FCST, every month Jan through ${MONTHS[cardMonth]} added together.`,
      actL: `Jan–${MONTHS[cardMonth]} ACT`, fcstL: `Jan–${MONTHS[cardMonth]} FCST`, accent: 'accent-slate',
      dm: ytdMonths, dl: `Jan–${MONTHS[cardMonth]} ${norm.year} year to date` },
  ];

  // ---- list ----
  const child = NEXT_DIM[groupKey];
  const nodeOf = (k, recs, d) => {
    const info = infoOf(recs[0]);
    const name = D[d].name(k, recs[0]);
    const short = d === 'project' ? shortProjectName({ name, account: { name: info.county, state: info.state } }) : name;
    return { k, d, name, short, recs, a: agg(recs, months), info };
  };
  function infoOf(r) {
    const p = norm.projects.get(r?.p) || {};
    return { state: p.state || '', county: p.county || '', type: r ? contractType(p) : '', pm: p.pm || '' };
  }
  const keep = (n) => Math.abs(n.a.pct ?? 0) >= minPct && Math.abs(n.a.var) >= minAbs
    && (!statusFilter || status(n.a.pct, band, n.a.var) === statusFilter);
  // Project rows read like the Milestone Tracker: State, County, Project, sorted in that order.
  const byProject = groupKey === 'project';
  const sortVal = (n) => (sort.key === 'name' ? (byProject ? `${n.info.state}|${n.info.county}|${n.short}` : n.name)
    : sort.key === 'short' ? n.short : INFO.includes(sort.key) ? n.info[sort.key] : n.a[sort.key]);
  const cmp = (x, y) => {
    const a = sortVal(x), b = sortVal(y);
    if (a == null) return 1;
    if (b == null) return -1;
    const d = typeof a === 'string' ? a.localeCompare(b) : a - b;
    return sort.dir === 'asc' ? d : -d;
  };
  const top = [...groupBy(forDim(records, groupKey), D[groupKey].key)].map(([k, recs]) => nodeOf(k, recs, groupKey)).filter(keep).sort(cmp);
  const shown = COLS.filter(([k]) => cols.includes(k) && !(byProject && (k === 'state' || k === 'county')));
  // Header band per column group, and a light divider where each non-info group starts.
  const groupSpans = shown.reduce((acc, [k, , g]) => {
    const lastSpan = acc[acc.length - 1];
    if (lastSpan && lastSpan.g === g) lastSpan.n += 1; else acc.push({ g, n: 1, start: k });
    return acc;
  }, []);
  const groupStarts = new Set(groupSpans.filter((s) => s.g !== 'info').map((s) => s.start));
  const divider = { borderLeft: `2px solid ${GROUP_LINE}` };
  const lineAt = (k) => (groupStarts.has(k) ? divider : {});
  const ROW_TINT = { good: 'is-done', warn: 'is-dueSoon', bad: 'is-overdue' };
  const arrow = (k) => (sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const toggleRow = (k) => setOpen((st) => { const x = new Set(st); if (x.has(k)) x.delete(k); else x.add(k); return x; });
  const cell = (n, k) => {
    const a = n.a;
    if (INFO.includes(k)) return n.d === 'project' || (n.d === 'county' && k === 'state') ? n.info[k] : '';
    if (k === 'rate') return a.rate ? `$${a.rate.toFixed(2)}` : '—';
    if (k.endsWith('H')) return hrs(a[k]);
    if (k === 'var') return <VarTxt v={a.var} pct={a.pct} band={band} text={moneyS(a.var)} bold />;
    if (k === 'psVar' || k === 'rdVar') return <VarTxt v={a[k]} pct={a[k === 'psVar' ? 'psPct' : 'rdPct']} band={band} text={moneyS(a[k])} />;
    if (k === 'pct') return a.pct == null ? '—' : <VarTxt v={a.pct} pct={a.pct} band={band} text={pctTxt(a.pct)} bold />;
    if (k === 'volVar' || k === 'rateVar') return <VarTxt v={a[k]} band={band} text={moneyS(a[k])} />;
    return money(a[k]);
  };
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' || INFO.includes(key) ? 'asc' : 'desc' }));
  const total = agg(records, months);

  function exportExcel() {
    const rows = [];
    for (const n of top) {
      for (const c of [...groupBy(n.recs, D[child].key)].map(([k, recs]) => nodeOf(k, recs, child))) {
        rows.push({
          [D[groupKey].label]: n.name, [D[child].label]: c.name, State: c.info.state, County: c.info.county, Type: c.info.type,
          'ACT hours': Math.round(c.a.outH), 'FCST hours': Math.round(c.a.baseH), 'ACT $': Math.round(c.a.out),
          'FCST $': Math.round(c.a.base), 'Var $': Math.round(c.a.var), 'Var %': c.a.pct == null ? '' : Number(c.a.pct.toFixed(1)),
          'Hours var $': Math.round(c.a.volVar), 'Rate var $': Math.round(c.a.rateVar),
        });
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Revenue');
    XLSX.writeFile(wb, `Revenue Tracker ${periodLabel}.xlsx`);
  }

  const optBtn = (k, t, d) => (
    <button key={k} type="button" className="card" onClick={() => setOpt(k)} aria-pressed={opt === k}
      style={{ flex: '1 1 220px', textAlign: 'left', cursor: 'pointer', padding: '8px 12px', font: 'inherit', color: 'inherit', boxShadow: opt === k ? 'inset 0 -3px 0 var(--primary-light)' : undefined, borderColor: opt === k ? 'var(--primary-light)' : undefined }}>
      <div style={{ fontWeight: 700, color: 'var(--heading)' }}>{t}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{d}</div>
    </button>
  );

  return (
    <div id="rt-page">
      <div className="page-header">
        <div className="page-eyebrow">Financials</div>
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>Revenue Tracker <ExportMenu label="Revenue Tracker" targetId="rt-page" /></h1>
        <p className="page-subtitle">ACT vs FCST revenue: hours × each project's billing rate in effect that month, compared to a locked forecast.
          {updatedAt && <span style={{ color: 'var(--text-light)' }}> Timesheet hours as of {updatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}, refreshed every 15 minutes.</span>}</p>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {optBtn('F', 'ACT/FCST', 'Revenue by month: by project, team and more, plus POC (Milestone)')}
        {optBtn('A', 'Variance', 'Cards, chart, breakdown, list / calendar, bridge, heatmaps')}
      </div>

      <div className="card" style={{ marginBottom: 12, padding: '10px 12px' }}>
        <div className="mt-toolbar">
          <select className="mt-ctl" value={norm.year} onChange={(e) => { setYear(Number(e.target.value)); setBaselineId(''); setMonth(null); }} aria-label="Year">
            {[norm.year - 1, norm.year, norm.year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-mid)', cursor: 'help' }} title={compareHelp(norm.baseline)}>Compare to ⓘ</span>
          <select className="mt-ctl" style={{ maxWidth: 320 }} value={norm.baseline?.id || ''} onChange={(e) => setBaselineId(e.target.value)} aria-label="Compare to" title={compareHelp(norm.baseline)}>
            <option value="">{compareOption(null)}</option>
            {data.baselines.map((b) => <option key={b.id} value={b.id}>{compareOption(b)}</option>)}
          </select>
          <button type="button" className="mt-ctl" disabled={focusMonth === 0} onClick={() => setMonth(focusMonth - 1)} title="Previous month" aria-label="Previous month">◀</button>
          <select className="mt-ctl" value={focusMonth} onChange={(e) => { const m = Number(e.target.value); setMonth(m); if (m === current) setLive(true); }} aria-label="Month">
            {ALL_MONTHS.map((m) => <option key={m} value={m}>{MONTHS[m]} {norm.year}{norm.closed.has(m) ? '' : ' (FCST)'}</option>)}
          </select>
          <button type="button" className="mt-ctl" disabled={focusMonth === 11} onClick={() => setMonth(focusMonth + 1)} title="Next month" aria-label="Next month">▶</button>
          {current != null && (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--text-mid)' }}
              title={`${MONTHS[current]} isn't ACT until its timesheets are certified at month-end. "Hours logged so far" shows it at the hours entered to date, updating as timesheets come in.`}>
              {MONTHS[current]} shows
              <div className="mt-seg">
                {[[false, 'FCST'], [true, 'Hours logged so far']].map(([k, l]) => <button key={l} type="button" aria-pressed={live === k} onClick={() => setLive(k)}>{l}</button>)}
              </div>
            </span>
          )}
          <div className="mt-seg">
            {[['all', 'PS + R&D'], ['PS', 'PS'], ['RD', 'R&D']].map(([k, l]) => <button key={k} type="button" aria-pressed={stream === k} onClick={() => setStream(k)}>{l}</button>)}
          </div>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--text-mid)' }}>
            {opt === 'A' && <>Under-FCST tolerance
            <select className="mt-ctl" value={band} onChange={(e) => setBand(Number(e.target.value))} aria-label="Under-forecast tolerance">{[1, 2, 3, 5, 10].map((v) => <option key={v} value={v}>{v}%</option>)}</select>
            Show rows ≥
            <select className="mt-ctl" value={minPct} onChange={(e) => setMinPct(Number(e.target.value))} aria-label="Minimum variance percent">{[0, 2, 5, 10, 20].map((v) => <option key={v} value={v}>{v ? `${v}% var` : 'any %'}</option>)}</select>
            <select className="mt-ctl" value={minAbs} onChange={(e) => setMinAbs(Number(e.target.value))} aria-label="Minimum variance dollars">{[0, 5000, 10000, 25000, 50000].map((v) => <option key={v} value={v}>{v ? `${money(v)} var` : 'any $'}</option>)}</select></>}
            Sort
            <select className="mt-ctl" value={rowOrder} onChange={(e) => pickOrder(e.target.value)} aria-label="Sort rows" title="Row order for every table, heatmap and list on this page">{ROW_ORDERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </span>
        </div>
        <div className="mt-toolbar" style={{ marginTop: 8 }}>
          {/* Pick one or more package lines; at least one stays on. */}
          <div className="mt-seg" title="Package lines">
            {LINE_ORDER.map((l) => (
              <button key={l} type="button" aria-pressed={shownLines.includes(l)} title={LINE_NOTE[l]}
                onClick={() => setShownLines((s) => (!s.includes(l) ? [...s, l] : s.length > 1 ? s.filter((x) => x !== l) : s))}>{l}</button>
            ))}
          </div>
          {['state', 'county', 'project', 'team', 'pm'].map((k) => (
            <MultiSelect key={k} label={{ type: 'Contract types', county: 'Counties' }[k] || `${D[k].label}s`} options={optionsFor(k)} selected={filters[k]} onChange={(v) => setFilters((f) => ({ ...f, [k]: v }))} />
          ))}
          <button type="button" className="mt-ctl" onClick={() => { setFilters({ project: [], team: [], state: [], county: [], pm: [] }); setStatusFilter(''); }}>Show all</button>
          {opt === 'A' && <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-mid)' }}>
            <span style={{ color: ST.good.color }}>▲ at or over FCST</span> · <span style={{ color: ST.warn.color }}>▼ up to {band}% under</span> · <span style={{ color: ST.bad.color }}>▼ more than {band}% under</span>
          </span>}
        </div>
      </div>


      {/* ACT & FCST tab: plain revenue, no variance slant (Judy, 2026-11-02). The page filters, package
          lines and Sort drive it; every table sorts by its column headers too. */}
      {opt === 'F' && (
        <>
          <div id="rt-af-month" className="card" style={{ minWidth: 0, marginBottom: 12 }}>
            <div className="mt-toolbar" style={{ justifyContent: 'space-between' }}>
              <span className="card-title">ACT/FCST by month · {norm.year}</span>
              <ExportMenu label="ACT and FCST by month" targetId="rt-af-month" />
            </div>
            <MonthTable plain records={records} closed={norm.closed} band={band} baseShort={baseShort} />
          </div>
          <div id="rt-af-breakdown" className="card" style={{ minWidth: 0, marginBottom: 12 }}>
            <div className="mt-toolbar" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="mt-seg" title="What the cells show">
                  {[['both', 'ACT + FCST'], ['act', 'ACT $'], ['fcst', 'FCST $']].map(([k, l]) => <button key={k} type="button" aria-pressed={afMetric === k} onClick={() => setAfMetric(k)}>{l}</button>)}
                </div>
                <span className="card-title">by month, broken down by</span>
                <select className="mt-ctl" value={afBreakdown} onChange={(e) => setAfBreakdown(e.target.value)} aria-label="Revenue broken down by">
                  <option value="stream">PS vs R&D</option>
                  {Object.entries(D).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                </select>
              </span>
              <ExportMenu label="ACT and FCST breakdown" targetId="rt-af-breakdown" />
            </div>
            <StreamTable records={records} closed={norm.closed} band={band} D={D} dim={afBreakdown} order={rowOrder} metric={afMetric} liveMonth={toDate ? current : null} />
          </div>
          {(() => {
            const shownP = new Set(records.filter((r) => r.kind === 'signoff').map((r) => r.p));
            const list = norm.signoffs.filter((s) => shownP.has(s.projectId) && shownLines.includes(s.line));
            return list.length > 0 && <MilestoneCard list={list} norm={norm} order={rowOrder} />;
          })()}
        </>
      )}

      {opt === 'A' && (<>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}><ExportMenu label="Variance cards" targetId="rt-cards" /></div>
      <div id="rt-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12, marginBottom: 12 }}>
        {cards.map((c) => (
          <button key={c.id} id={c.id} type="button" className={`card mt-card ${c.accent}`} onClick={() => openDrill(c.label, records, 'project', c.dm, c.dl)}
            style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: '12px 16px' }}>
            <div className="card-title">{c.label}</div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 2 }}>{c.sub}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '6px 0' }}>
              <MonthGauge size={120} pct={c.a.base && !c.a.future ? (c.a.out / c.a.base) * 100 : null} dayPct={c.dayPct} color={varFill(c.a.pct, band)} />
              <div style={{ color: c.a.pct == null ? 'var(--text-light)' : varColor(c.a.pct, band), flex: 1 }}>
                <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.1 }}>{moneyS(c.a.var)}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{c.a.pct == null ? 'not started' : `${ST[status(c.a.pct, band)].icon} ${pctTxt(c.a.pct)}`}</div>
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div><div style={{ fontSize: 20, fontWeight: 700 }}>{money(c.a.out)}</div><div style={{ fontSize: 12, color: 'var(--text-mid)' }}>{c.actL}</div></div>
                <div><div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-mid)' }}>{money(c.a.base)}</div><div style={{ fontSize: 12, color: 'var(--text-mid)' }}>{c.fcstL}</div></div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              {hrs(c.a.outH)} of {hrs(c.a.baseH)} FCST hours · ring = ACT as % of FCST
              {c.dayPct != null && <> · {c.dayPct}% through the month (black tick)</>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr', gap: '2px 10px', fontSize: 12, marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
              <span /><span style={{ color: 'var(--text-light)' }}>ACT</span><span style={{ color: 'var(--text-light)' }}>FCST</span><span style={{ color: 'var(--text-light)' }}>Var</span>
              {[['PS', 'ps', 'psBase', 'psVar', 'psPct'], ['R&D', 'rd', 'rdBase', 'rdVar', 'rdPct']].map(([l, a, b, v, p]) => (
                <React.Fragment key={l}>
                  <b>{l}</b><span>{money(c.a[a])}</span><span>{money(c.a[b])}</span>
                  <VarTxt v={c.a[v]} pct={c.a[p]} band={band} text={moneyS(c.a[v])} bold />
                </React.Fragment>
              ))}
            </div>
          </button>
        ))}
      </div>
      {(() => {
        // Projects priced at a placeholder rate (or none) this month, so nobody reads them as final.
        const onPage = records;
        const flag = (pick) => [...new Set(onPage.filter((r) => r.m === cardMonth && pick(r)).map((r) => norm.projects.get(r.p)?.name || r.p))];
        const temp = [...new Map(onPage.filter((r) => r.m === cardMonth && r.tempRate != null).map((r) => [r.p, `${norm.projects.get(r.p)?.name || r.p} (${money(r.tempRate)}/hr)`])).values()];
        const none = flag((r) => r.noRate);
        const flags = (temp.length || none.length) && (
          <div className="notice notice-info" style={{ marginBottom: 12, fontSize: 12 }}>
            {temp.length > 0 && <div>⚠ Temporary rate until Finance confirms: {temp.join(', ')}</div>}
            {none.length > 0 && <div>⚠ No rate — hours priced at $0: {none.join(', ')}</div>}
          </div>
        );
        return flags || null;
      })()}
      {(() => {
        // The over/under project split -- same month as the Month variance card.
        const byProj = [...groupBy(records.filter((r) => r.m === cardMonth), (r) => r.p)]
          .map(([k, recs]) => ({ k, name: norm.projects.get(k)?.name || k, a: agg(recs, [cardMonth]) }))
          .filter((p) => !p.a.future && (p.a.out || p.a.base));
        const buckets = ['good', 'warn', 'bad'].map((s) => {
          const list = byProj.filter((p) => status(p.a.pct, band, p.a.var) === s).sort(rowCompare(rowOrder, (p) => p.name, (p) => p.a.var));
          return { s, list, total: list.reduce((t, p) => t + p.a.var, 0) };
        });
        const pickStatus = (s) => {
          setStatusFilter((cur) => (cur === s ? '' : s));
          setOpt('A'); setView('list'); setGroupKey('project'); setGrain('month'); setMonth(cardMonth);
        };
        return (
          <div style={{ marginBottom: 12 }}>
            <div id="rt-overunder" className="card mt-card accent-slate" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span className="card-title">Projects over / under FCST · {MONTHS[cardMonth]}</span>
                <ExportMenu label="Projects over and under FCST" targetId="rt-overunder" />
                {statusFilter && <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setStatusFilter('')}>Show all</button>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-mid)', margin: '4px 0 8px' }}>Click a group to filter the list to it; click it again or Show all to clear.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                {buckets.map((b) => (
                  <button key={b.s} type="button" onClick={() => pickStatus(b.s)} aria-pressed={statusFilter === b.s}
                    style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', background: ST[b.s].bg, border: `1px solid ${statusFilter === b.s ? ST[b.s].color : 'transparent'}`, borderRadius: 8, padding: '8px 10px', minWidth: 0 }}>
                    <div style={{ color: ST[b.s].color, fontWeight: 700, fontSize: 14 }}>{ST[b.s].icon} {ST[b.s].label}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 28, fontWeight: 700 }}>{b.list.length}</span>
                      <span style={{ fontSize: 16, color: ST[b.s].color, fontWeight: 700 }}>{moneyS(b.total)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 6, display: 'grid', gap: 3 }}>
                      {b.list.slice(0, 5).map((p) => (
                        <div key={p.k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }} title={`${p.name}: ${moneyS(p.a.var)}${hoursTxt(p.a) ? ` · ${hoursTxt(p.a)}` : ''}`}>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                          <span style={{ whiteSpace: 'nowrap' }}><b style={{ color: ST[b.s].color }}>{moneyS(p.a.var)}</b>{hoursTxt(p.a) && <span style={{ fontSize: 12 }}> ({hoursTxt(p.a)})</span>}</span>
                        </div>
                      ))}
                      {b.list.length > 5 && <div>+{b.list.length - 5} more</div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
      {norm.baseline && (
        <div className="card" style={{ marginBottom: 12, padding: '10px 14px', fontSize: 13, color: 'var(--text-mid)' }}>
          {MONTHS[cardMonth]} came in <b style={{ color: varColor(mo.pct, band) }}>{moneyS(mo.var)}</b> vs forecast, and the full year moved <b style={{ color: varColor(fy.pct, band) }}>{moneyS(fy.var)}</b> vs {baselineLabel(norm.baseline)}.
          {' '}The months still to come {fy.var - mo.var >= 0 ? 'went up' : 'came down'} by about <b>{money(Math.abs(fy.var - mo.var))}</b>.
        </div>
      )}
      </>)}

      {opt === 'A' && (
        <>
          {/* One card: the chart, then the breakdown under it -- its Total row is the monthly variance, so
              the old ACT/FCST/var table here only repeated it and the ACT & FCST tab (Judy, 2026-11-02). */}
          <div id="rt-chart-month" className="card" style={{ minWidth: 0, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span className="card-title">Variance by month · {norm.year} · YTD <span style={{ color: varColor(ytd.pct, band) }}>{moneyS(ytd.var)}{ytd.pct != null ? ` (${pctTxt(ytd.pct)})` : ''}</span></span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {grain !== 'year' && <button type="button" className="btn btn-outline" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => { setGrain('year'); setMonth(null); }}>Show full year</button>}
                <ExportMenu label="Variance by month" targetId="rt-chart-month" />
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Bars: each month's variance on its own. Line: running total from January. Faded bars are months still at FCST. Click a bar, or a month in the Total row below, to focus the list on that month; click it again to go back to the full year.</div>
            <RevenueChart records={records} closed={norm.closed} band={band} baseShort={baseShort} selected={months} onMonth={focusOn} />
            <div className="mt-toolbar" style={{ margin: '12px 0 8px' }}>
              <span className="card-title" style={{ marginBottom: 0 }}>Broken down by</span>
              <select className="mt-ctl" value={breakdown} onChange={(e) => setBreakdown(e.target.value)} aria-label="Break down by">
                <option value="stream">PS vs R&D</option>
                {Object.entries(D).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
              </select>
            </div>
            <StreamTable records={records} closed={norm.closed} band={band} D={D} dim={breakdown} order={rowOrder} metric="var" onMonth={focusOn} focused={grain === 'month' ? focusMonth : null} />
          </div>
          <div className="card">
            <div className="mt-toolbar" style={{ marginBottom: 10 }}>
              <div className="mt-seg">{[['list', 'List'], ['calendar', 'Calendar']].map(([k, l]) => <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>{l}</button>)}</div>
              {view === 'list' && (
                <>
                  <select className="mt-ctl" value={groupKey} onChange={(e) => { setGroupKey(e.target.value); setOpen(new Set()); }} aria-label="Group by">
                    {Object.entries(D).map(([k, d]) => <option key={k} value={k}>Group by {d.label.toLowerCase()}</option>)}
                  </select>
                  <div style={{ position: 'relative' }}>
                    <button type="button" className="mt-ctl" onClick={() => setColsOpen((v) => !v)}>Columns ▾</button>
                    {colsOpen && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setColsOpen(false)} />
                        <div className="mt-pop">
                          {COLS.map(([k, l, g]) => (
                            <label key={k} className="mt-check" style={{ display: 'flex', height: 'auto', padding: '3px 0' }}>
                              <input type="checkbox" checked={cols.includes(k)} onChange={() => setCols((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]))} />{g === 'ps' || g === 'rd' ? `${GROUPS[g]} ${l}` : l}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button type="button" className="mt-ctl" onClick={() => setOpen(new Set(top.map((n) => n.k)))}>Expand all</button>
                  <button type="button" className="mt-ctl" onClick={() => setOpen(new Set())}>Collapse all</button>
                </>
              )}
              {statusFilter && (
                <button type="button" className="mt-ctl" onClick={() => setStatusFilter('')} style={{ color: ST[statusFilter].color, borderColor: ST[statusFilter].color }}>
                  Only {ST[statusFilter].label.toLowerCase()} ×
                </button>
              )}
              {view === 'list' && (
                <>
                  <div className="mt-seg" title="Period the list and the bridge below cover">
                    {[['month', 'Month', 'One month on its own'], ['qtr', 'Quarter total', 'Three months added together'], ['year', 'Year total', 'Jan–Dec added together']].map(([k, l, t]) => <button key={k} type="button" title={t} aria-pressed={grain === k} onClick={() => setGrain(k)}>{l}</button>)}
                  </div>
                  {grain === 'qtr' && (
                    <select className="mt-ctl" value={qtr} onChange={(e) => setQtr(Number(e.target.value))} aria-label="Quarter">
                      {[0, 1, 2, 3].map((q) => <option key={q} value={q}>Q{q + 1} {norm.year}</option>)}
                    </select>
                  )}
                </>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-light)' }} title={periodHelp}>{periodLabel}</span>
              <ExportMenu label={`Revenue Tracker ${periodLabel}`} targetId="rt-list" extraExcel={view === 'list' ? exportExcel : undefined} />
            </div>
            {view === 'list' && <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 8 }}><b>Period:</b> {periodHelp} The bridge at the bottom covers the same period.</div>}
            {view === 'list' ? (
              <div id="rt-list" className="sheet-wrap" style={{ maxHeight: 640, overflow: 'auto' }}>
                <table className="sheet mt-grid">
                  <thead>
                    <tr>
                      <th colSpan={byProject ? 3 : 1} />
                      {groupSpans.map((s) => (
                        <th key={s.g + s.start} colSpan={s.n} style={{ textAlign: 'center', fontSize: 11, color: 'var(--heading)', ...(s.g !== 'info' ? divider : {}) }}>{GROUPS[s.g]}</th>
                      ))}
                    </tr>
                    <tr>
                      {byProject ? (
                        <>
                          <th style={{ cursor: 'pointer' }} onClick={() => onSort('name')}>State{arrow('name')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => onSort('county')}>County{arrow('county')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => onSort('short')}>Project{arrow('short')}</th>
                        </>
                      ) : (
                        <th style={{ cursor: 'pointer' }} onClick={() => onSort('name')}>{D[groupKey].label} › {D[child].label}{arrow('name')}</th>
                      )}
                      {shown.map(([k, l]) => (
                        <th key={k} style={{ textAlign: INFO.includes(k) ? 'left' : 'right', cursor: 'pointer', ...lineAt(k) }} onClick={() => onSort(k)}>{l}{arrow(k)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {top.length === 0 && <tr><td colSpan={shown.length + 3} style={{ ...TDL, color: 'var(--text-light)' }}>No rows pass the variance threshold. Lower "Show rows ≥" to see more.</td></tr>}
                    {top.map((n) => (
                      <React.Fragment key={n.k}>
                        <tr className={`mt-row ${ROW_TINT[status(n.a.pct, band, n.a.var)]}`}>
                          {byProject ? (
                            <>
                              <td style={{ ...TDL, cursor: 'pointer' }} onClick={() => toggleRow(n.k)}>
                                <span style={{ display: 'inline-block', width: 14, color: 'var(--text-light)' }}>{open.has(n.k) ? '▾' : '▸'}</span>{n.info.state}
                              </td>
                              <td style={TDL}>{countyLabel({ account: { name: n.info.county } })}</td>
                              <td style={{ ...TDL, cursor: 'pointer' }} title={n.name} onClick={() => toggleRow(n.k)}>{n.short}</td>
                            </>
                          ) : (
                            <td style={{ ...TDL, cursor: 'pointer' }} onClick={() => toggleRow(n.k)}>
                              <span style={{ display: 'inline-block', width: 14, color: 'var(--text-light)' }}>{open.has(n.k) ? '▾' : '▸'}</span>{n.name}
                            </td>
                          )}
                          {shown.map(([k]) => <td key={k} style={{ ...(INFO.includes(k) ? TDL : TD), cursor: 'pointer', ...lineAt(k) }} onClick={() => openDrill(n.name, n.recs, child)}>{cell(n, k)}</td>)}
                        </tr>
                        {open.has(n.k) && [...groupBy(n.recs, D[child].key)].map(([k, recs]) => nodeOf(k, recs, child)).sort(cmp).map((c) => (
                          <tr key={`${n.k}-${c.k}`}>
                            <td colSpan={byProject ? 3 : 1} style={{ ...TDL, paddingLeft: 32, color: 'var(--text-mid)' }}>{c.name}</td>
                            {shown.map(([k]) => <td key={k} style={{ ...(INFO.includes(k) ? TDL : TD), cursor: 'pointer', ...lineAt(k) }} onClick={() => openDrill(`${n.name} › ${c.name}`, c.recs, NEXT_DIM[child])}>{cell(c, k)}</td>)}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    <tr className="is-group">
                      <td colSpan={byProject ? 3 : 1}>Total · {periodLabel}</td>
                      {shown.map(([k]) => <td key={k} style={{ ...TD, ...lineAt(k) }}>{INFO.includes(k) ? '' : cell({ a: total, info: {}, d: '' }, k)}</td>)}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div id="rt-list"><CalendarView records={records} closed={norm.closed} band={band} baseShort={baseShort} year={norm.year} onMonth={(m) => { setGrain('month'); setMonth(m); setView('list'); }} /></div>
            )}
          </div>
        </>
      )}

      {opt === 'A' && (
        <>
          <Heatmap order={rowOrder} records={records} D={D} dim={hmDim} setDim={setHmDim} band={band} minPct={minPct} minAbs={minAbs} closed={norm.closed} baseShort={baseShort} onOpen={openDrill} />
          <Heatmap streams records={records} D={D} band={band} closed={norm.closed} baseShort={baseShort} onOpen={openDrill} />
          {/* Bridge and billing rate changes sit last, under the heatmaps (Judy, 2026-11-02). */}
          <Bridge records={records} months={months} band={band} norm={norm} periodLabel={periodLabel} baseShort={baseShort} />
        </>
      )}

      {drill && <Drawer order={rowOrder} drill={drill} D={D} months={drill.months || months} band={band} closed={norm.closed} baseShort={baseShort} periodLabel={drill.label || periodLabel} onClose={() => setDrill(null)} />}
    </div>
  );
}

// Each month by the chosen breakdown (PS vs R&D, team, project, ...) as variance, ACT or FCST
// revenue, plus YTD and year totals. Months without actuals are shaded; 'both' is one row: ACT, then FCST.
// Rows follow the page's Sort until a column header is clicked.
const FCST_TINT = 'color-mix(in srgb, var(--primary-light) 12%, transparent)';
function StreamTable({ records, closed, band, D, dim, order, metric, onMonth, focused, liveMonth }) {
  const [colSort, setColSort] = useState(null); // { col: 'name' | month | 'ytd' | 'year', dir }
  const done = ALL_MONTHS.filter((m) => closed.has(m));
  const last = done.length ? MONTHS[Math.max(...done)] : '';
  const recs0 = forDim(records, dim);
  const colMonths = (c) => (c === 'ytd' || (c === 'year' && metric === 'act') ? done : c === 'year' ? ALL_MONTHS : [c]);
  // The number a cell shows for `recs` over `ms`; null renders as '—'.
  const valOf = (recs, ms) => {
    if (metric === 'both') return ms.reduce((t, m) => { const a = agg(recs, [m]); return t + (closed.has(m) ? a.out : a.base); }, 0);
    const a = agg(recs, ms);
    return metric === 'act' ? (a.future ? null : a.out) : metric === 'fcst' ? a.base : a.var;
  };
  let groups = dim === 'stream'
    ? [['PS', recs0.filter((r) => r.stream === 'PS')], ['R&D', recs0.filter((r) => r.stream === 'RD')]]
    : [...groupBy(recs0, D[dim].key)].map(([k, recs]) => [D[dim].name(k, recs[0]), recs])
      .sort(rowCompare(order, (g) => g[0], (g) => valOf(g[1], colMonths('year'))));
  if (colSort) {
    const by = colSort.col === 'name' ? (g) => g[0] : (g) => valOf(g[1], colMonths(colSort.col)) ?? -Infinity;
    groups = [...groups].sort((a, b) => {
      const x = by(a), y = by(b);
      const d = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return colSort.dir === 'asc' ? d : -d;
    });
  }
  const sortOn = (col) => setColSort((s) => (s?.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'name' ? 'asc' : 'desc' }));
  const arrow = (col) => (colSort?.col === col ? (colSort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const rows = [...groups, ['Total', recs0]];
  const line = { borderLeft: `2px solid ${GROUP_LINE}` };
  const cell = (recs, col) => {
    const ms = colMonths(col);
    if (metric === 'var') { const a = agg(recs, ms); return a.var == null ? '—' : <VarTxt v={a.var} pct={a.pct} band={band} text={moneyS(a.var)} />; }
    const x = valOf(recs, ms);
    return x == null ? '—' : Math.abs(x) < 0.5 ? '' : money(x);
  };
  const both = metric === 'both';
  const tint = (m) => (!closed.has(m) ? { background: FCST_TINT, fontStyle: 'italic' } : {});
  const th = (col, label, style, title) => <th style={{ textAlign: 'right', cursor: 'pointer', ...style }} title={title} onClick={() => sortOn(col)}>{label}{arrow(col)}</th>;
  const yearTitle = metric === 'act' ? 'ACT, every month with actuals' : both ? 'ACT through the last closed month plus FCST after' : 'Jan–Dec added together; months without actuals count at FCST';
  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => sortOn('name')}>{{ var: 'Variance $', act: 'ACT $', fcst: 'FCST $', both: 'Revenue $' }[metric]}{arrow('name')}</th>
            {ALL_MONTHS.map((m) => th(m, `${MONTHS[m]}${both || closed.has(m) ? '' : ' (FCST)'}`, { fontStyle: closed.has(m) ? 'normal' : 'italic', ...tint(m) }))}
            {th('ytd', `YTD total${last ? ` (Jan–${last})` : ''}`, line, 'Sum of every month with actuals')}
            {th('year', 'Year total', line, yearTitle)}
          </tr>
          {both && (
            <tr>
              <th />
              {ALL_MONTHS.map((m) => <th key={m} style={{ textAlign: 'right', fontSize: 10, fontWeight: 600, color: closed.has(m) ? 'var(--text-mid)' : 'var(--primary-light)', ...tint(m) }}>{closed.has(m) ? (m === liveMonth ? 'ACT to date' : 'ACT') : 'FCST'}</th>)}
              <th style={{ textAlign: 'right', fontSize: 10, fontWeight: 600, color: 'var(--text-mid)', ...line }}>ACT</th>
              <th style={{ textAlign: 'right', fontSize: 10, fontWeight: 600, color: 'var(--text-mid)', ...line }}>ACT + FCST</th>
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map(([l, recs]) => (
            <tr key={l} className={l === 'Total' ? 'is-group' : undefined}>
              <td style={{ ...TDL, fontWeight: 700 }}>{l}</td>
              {ALL_MONTHS.map((m) => {
                const pick = onMonth && l === 'Total';
                return <td key={m} style={{ ...TD, ...tint(m), ...(focused === m ? { background: 'var(--hover-tint)' } : {}), ...(pick ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}) }} title={pick ? `Focus the list on ${MONTHS[m]}` : undefined} onClick={pick ? () => onMonth(m) : undefined}>{cell(recs, m)}</td>;
              })}
              <td style={{ ...TD, ...line }}>{cell(recs, 'ytd')}</td>
              <td style={{ ...TD, ...line }}>{cell(recs, 'year')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CalendarView({ records, closed, band, baseShort, year, onMonth }) {
  const run = runningVariance(records);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
      {[0, 1, 2, 3].map((q) => {
        const qa = agg(records, [q * 3, q * 3 + 1, q * 3 + 2]);
        return (
          <React.Fragment key={q}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
              <b style={{ color: 'var(--heading)' }}>Q{q + 1} {year}</b>
              <span style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>ACT {money(qa.out)} <span style={{ color: 'var(--text-light)' }}>{baseShort} {money(qa.base)}</span><Chip pct={qa.pct} band={band} /></span>
            </div>
            {[q * 3, q * 3 + 1, q * 3 + 2].map((m) => {
              const a = agg(records, [m]);
              const s = status(a.pct, band, a.var);
              return (
                <button key={m} type="button" onClick={() => onMonth(m)} className="card"
                  style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', borderTop: `4px solid ${a.base ? ST[s].fill : 'var(--border)'}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ display: 'flex', justifyContent: 'space-between' }}><b>{MONTHS[m]}</b><span style={{ fontSize: 11, color: 'var(--text-light)' }}>{closed.has(m) ? 'ACT' : 'FCST'}</span></span>
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>{closed.has(m) ? 'ACT' : 'FCST'}</span><b>{money(a.out)}</b></span>
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-mid)' }}><span>FCST</span><span>{money(a.base)}</span></span>
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ color: ST[s].color, fontWeight: 600 }}>{moneyS(a.var)}</span><Chip pct={a.pct} band={band} /></span>
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-mid)' }}><span>Running total</span><span>{moneyS(run[m].running)}</span></span>
                </button>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// `streams` fixes the rows to PS and R&D -- no row picker or threshold, so they always add up to the total.
function Heatmap({ records, D, dim, setDim, band, minPct, minAbs, closed, baseShort, onOpen, streams, order }) {
  const rows = streams
    ? ['PS', 'RD'].map((k) => { const recs = records.filter((r) => r.stream === k); return { k, name: k === 'RD' ? 'R&D' : 'PS', recs, y: agg(recs, ALL_MONTHS) }; })
    : [...groupBy(forDim(records, dim), D[dim].key)]
      .map(([k, recs]) => ({ k, name: D[dim].name(k, recs[0]), recs, y: agg(recs, ALL_MONTHS) }))
      .filter((n) => Math.abs(n.y.pct ?? 0) >= minPct && Math.abs(n.y.var) >= minAbs)
      .sort(rowCompare(order, (n) => n.name, (n) => n.y.var));
  const id = streams ? 'rt-heatmap-stream' : 'rt-heatmap';
  // Totals over the rows shown, so a variance threshold filters the total too.
  const totalRecs = rows.flatMap((n) => n.recs);
  const total = { k: '__total', name: 'Total', recs: totalRecs, y: agg(totalRecs, ALL_MONTHS) };
  const shade = (a) => {
    if (!a.base && !a.out) return undefined;
    if (a.future) return FCST_TINT; // no actuals yet: the same blue as the FCST months in the tables
    const s = status(a.pct, band, a.var);
    const pct = s === 'warn' ? 28 : Math.round(18 + Math.min(1, Math.abs(a.pct ?? 0) / 15) * 50);
    return `color-mix(in srgb, ${ST[s].fill} ${pct}%, var(--surface))`;
  };
  const Cell = ({ a, title, recs, line }) => (
    <td style={{ ...TD, background: shade(a), cursor: 'pointer', minWidth: 64, ...(line ? { borderLeft: `2px solid ${GROUP_LINE}` } : {}) }} title={`${title}\nACT ${money(a.out)}\n${baseShort} ${money(a.base)}\nVariance ${moneyS(a.var)}`} onClick={() => onOpen(title, recs, 'resource')}>
      {a.var != null && Math.abs(a.var) < 0.5 ? '' : <>{pctTxt(a.pct)}<span style={{ display: 'block', fontSize: 11, color: 'var(--text-mid)' }}>{moneyS(a.var)}</span></>}
    </td>
  );
  return (
    <div id={id} className="card" style={{ marginBottom: 12 }}>
      <div className="mt-toolbar" style={{ marginBottom: 10 }}>
        <ExportMenu label={streams ? 'PS vs R&D variance heatmap' : 'Variance heatmap'} targetId={id} />
        <div><div className="card-title">{streams ? 'PS vs R&D variance' : 'Variance heatmap'} · {baseShort}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{streams ? 'The same variance split into PS and R&D only; the two rows add up to the Total row. ' : ''}Green is at or over, orange up to {band}% under, red further under.{streams ? '' : ' Row order follows Sort, above.'}</div></div>
        {!streams && <select className="mt-ctl" style={{ marginLeft: 'auto' }} value={dim} onChange={(e) => setDim(e.target.value)} aria-label="Rows">
          {['project', 'team', 'resource', 'state', 'county', 'pm'].map((k) => <option key={k} value={k}>Rows: {D[k].label}</option>)}
        </select>}
      </div>
      <div className="sheet-wrap" style={{ maxHeight: 640, overflow: 'auto' }}>
        <table className="sheet">
          <thead><tr>
            <th className="is-frozen" style={{ left: 0 }}>{streams ? 'Stream' : D[dim].label}</th>
            {ALL_MONTHS.map((m) => <th key={m} style={{ textAlign: 'right', fontStyle: closed.has(m) ? 'normal' : 'italic' }}>{MONTHS[m]}{closed.has(m) ? '' : ' F'}</th>)}
            {[0, 1, 2, 3].map((q) => <th key={q} style={{ textAlign: 'right', ...(q === 0 ? { borderLeft: `2px solid ${GROUP_LINE}` } : {}) }} title={`${MONTHS[q * 3]}–${MONTHS[q * 3 + 2]} added together`}>Q{q + 1} total</th>)}
            <th style={{ textAlign: 'right', borderLeft: `2px solid ${GROUP_LINE}` }} title="Jan–Dec added together">Year total</th>
          </tr></thead>
          <tbody>
            {[...rows, total].map((n) => (
              <tr key={n.k} className={n === total ? 'is-group' : undefined}>
                <td className="is-frozen" style={{ ...TDL, left: 0, fontWeight: n === total ? 700 : undefined }}>{n.name}</td>
                {ALL_MONTHS.map((m) => <Cell key={m} a={agg(n.recs, [m])} title={`${n.name} · ${MONTHS[m]}`} recs={n.recs.filter((r) => r.m === m)} />)}
                {[0, 1, 2, 3].map((q) => <Cell key={q} a={agg(n.recs, [q * 3, q * 3 + 1, q * 3 + 2])} title={`${n.name} · Q${q + 1} total`} recs={n.recs.filter((r) => Math.floor(r.m / 3) === q)} line={q === 0} />)}
                <Cell a={n.y} title={`${n.name} · year total`} recs={n.recs} line />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Bridge({ records, months, band, norm, periodLabel, baseShort }) {
  const tot = agg(records, months);
  const items = [...groupBy(records, (r) => r.p)].map(([k, recs]) => ({ name: norm.projects.get(k)?.name || k, a: agg(recs, months) }))
    .filter((i) => Math.abs(i.a.var) >= 0.5).sort((a, b) => b.a.var - a.a.var);
  const steps = [{ l: 'FCST', v: tot.base, total: true }, ...items.map((i) => ({ l: i.name, v: i.a.var, pct: i.a.pct })), { l: 'ACT', v: tot.out, total: true }];
  const run = [];
  let acc = 0;
  for (const s of steps) {
    if (s.total) { run.push([0, s.v]); acc = s.v; } else { run.push([acc, acc + s.v]); acc += s.v; }
  }
  const all = run.flat();
  const sc = niceScale(Math.min(...all) * 0.97, Math.max(...all) * 1.02);
  const W = 900, H = 320, L = 64, R = 10, T = 18, B = 90;
  const pw = W - L - R, ph = H - T - B, gw = pw / Math.max(1, steps.length), bw = Math.min(46, gw * 0.6);
  const Y = (v) => T + ph - ((v - sc.min) / (sc.max - sc.min)) * ph;
  // Only changes that took effect in the year shown -- older history doesn't move this year's numbers.
  const rateChanges = [...norm.ratesBy.entries()].flatMap(([pid, list]) => list.slice(1).map((r, i) => ({ project: norm.projects.get(pid)?.name || pid, from: list[i].rate, to: r.rate, date: String(r.effectiveDate).slice(0, 10) })))
    .filter((r) => r.date.startsWith(String(norm.year)));
  const rate2 = (v) => `$${Number(v).toFixed(2)}`;
  return (
    <div id="rt-bridge" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12 }}>
      <div className="card" style={{ minWidth: 0, gridColumn: 'span 2' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span className="card-title">Bridge · FCST → ACT · {periodLabel}</span>
          <ExportMenu label="Bridge" targetId="rt-bridge" />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
          How total FCST becomes total ACT for {periodLabel}: start at the FCST bar, each project then adds (green, over FCST) or takes away (orange/red, under) its own variance, ending at the ACT bar. Read it to see which projects explain the gap: biggest overs on the left, biggest unders on the right.
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="Bridge from FCST to ACT">
          {ticks(sc).map((v) => <g key={v}><line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} stroke="var(--border)" strokeOpacity={0.6} /><text x={L - 6} y={Y(v) + 4} textAnchor="end" fontSize={11} fill="var(--text-light)">{money(v)}</text></g>)}
          {steps.map((s, i) => {
            const [a, b] = run[i];
            const x = L + gw * i + (gw - bw) / 2;
            const yTop = s.total ? Y(s.v) : Y(Math.max(a, b));
            const h = s.total ? Y(sc.min) - Y(s.v) : Math.max(1, Math.abs(Y(a) - Y(b)));
            return (
              <g key={i}>
                <rect x={x} y={yTop} width={bw} height={h} rx={3} fill={s.total ? 'var(--accent-slate)' : varFill(s.pct, band)}><title>{`${s.l}: ${s.total ? money(s.v) : moneyS(s.v)}`}</title></rect>
                <text x={x + bw / 2} y={yTop - 5} textAnchor="middle" fontSize={11} fontWeight={600} fill="var(--text-mid)">{s.total ? money(s.v) : moneyS(s.v)}</text>
                <text transform={`translate(${x + bw / 2},${H - B + 14}) rotate(-30)`} textAnchor="end" fontSize={11} fill="var(--text-light)">{s.l.length > 26 ? `${s.l.slice(0, 25)}…` : s.l}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="card" style={{ minWidth: 0 }}>
        <div className="card-title">Billing rate changes · {norm.year}</div>
        {rateChanges.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 6 }}>No rate changes on these projects in {norm.year}.</div> : (
          <div className="sheet-wrap" style={{ marginTop: 6, maxHeight: 260, overflow: 'auto' }}>
            <table className="sheet" style={{ width: '100%' }}>
              <thead><tr><th>Project</th><th>Effective</th><th style={{ textAlign: 'right' }}>Rate</th></tr></thead>
              <tbody>{rateChanges.map((r, i) => <tr key={i}><td style={{ ...TDL, whiteSpace: 'normal' }}>{r.project}</td><td style={TDL}>{r.date}</td><td style={TD}>{rate2(r.from)} → <b>{rate2(r.to)}</b></td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// POC (Milestone) and B&B revenue is recognized on sign-off. Laid out Jan–Dec like the variance table
// above it, with gross, COGS and net per project: a signed milestone sits in its signed month (ACT),
// an open one in its target month (FCST, italic). The variance tables count the net only.
function MilestoneCard({ list, norm, order }) {
  const inYear = (d) => d && String(d).startsWith(String(norm.year));
  const monthOf = (d) => Number(String(d).slice(5, 7)) - 1;
  const cells = new Map(); // `${projectId}|${m}` -> { gross, cogs, net, fcst, titles }
  for (const s of list) {
    const m = inYear(s.dateSigned) ? monthOf(s.dateSigned) : !s.dateSigned && inYear(s.targetDate) ? monthOf(s.targetDate) : null;
    if (m == null) continue;
    const k = `${s.projectId}|${m}`;
    const c = cells.get(k) || { gross: 0, cogs: 0, net: 0, fcst: false, titles: [] };
    c.gross += s.amount; c.cogs += s.cogs || 0; c.net += s.net; c.fcst = c.fcst || !s.dateSigned;
    c.titles.push(`${s.title}${s.dateSigned ? ` (signed ${String(s.dateSigned).slice(0, 10)})` : ' (FCST)'}`);
    cells.set(k, c);
  }
  const projects = [...new Set(list.map((s) => s.projectId))]
    .map((p) => ({ p, name: norm.projects.get(p)?.name || p,
      net: ALL_MONTHS.reduce((t, m) => t + (cells.get(`${p}|${m}`)?.net || 0), 0) }))
    .sort(order === 'alpha' ? (a, b) => a.name.localeCompare(b.name) : order === 'high' ? (a, b) => b.net - a.net : (a, b) => a.net - b.net);
  const ROWS = [['gross', 'Gross'], ['cogs', 'COGS'], ['net', 'Net']];
  const line = { borderLeft: `2px solid ${GROUP_LINE}` };
  const fmt = (v) => (Math.abs(v) < 0.5 ? '' : money(v));
  const totalOf = (key, ps) => ALL_MONTHS.map((m) => ps.reduce((t, x) => t + (cells.get(`${x.p}|${m}`)?.[key] || 0), 0));
  return (
    <div id="rt-milestones" className="card" style={{ minWidth: 0, marginBottom: 12 }}>
      <div className="mt-toolbar" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="card-title">POC (Milestone) · gross, COGS and net · {norm.year}</span>
        <ExportMenu label="POC (Milestone)" targetId="rt-milestones" />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 8 }}>Recognized on customer sign-off, in the month signed; <i>italic</i> is FCST, in the target month. The variance tables above count the net. PMs update amounts, COGS and dates on the project's Billing Milestones.</div>
      <div className="sheet-wrap" style={{ maxHeight: 520, overflow: 'auto' }}>
        <table className="sheet">
          <thead><tr>
            <th>Project</th><th />
            {ALL_MONTHS.map((m) => <th key={m} style={{ textAlign: 'right', fontStyle: norm.closed.has(m) ? 'normal' : 'italic' }}>{MONTHS[m]}{norm.closed.has(m) ? '' : ' (FCST)'}</th>)}
            <th style={{ textAlign: 'right', ...line }}>Year total</th>
          </tr></thead>
          <tbody>
            {projects.map((x) => ROWS.map(([key, label], ri) => (
              <tr key={`${x.p}|${key}`} className={key === 'net' ? 'is-group' : undefined}>
                <td style={{ ...TDL, fontWeight: 700 }}>{ri === 0 ? x.name : ''}</td>
                <td style={{ ...TDL, color: key === 'net' ? undefined : 'var(--text-mid)' }}>{label}</td>
                {ALL_MONTHS.map((m) => {
                  const c = cells.get(`${x.p}|${m}`);
                  return <td key={m} style={{ ...TD, fontStyle: c?.fcst ? 'italic' : undefined, color: c?.fcst ? 'var(--primary-light)' : undefined }} title={c?.titles.join('\n')}>{c ? fmt(c[key]) : ''}</td>;
                })}
                <td style={{ ...TD, ...line, fontWeight: 600 }}>{fmt(totalOf(key, [x]).reduce((t, v) => t + v, 0))}</td>
              </tr>
            )))}
            {projects.length > 1 && ROWS.map(([key, label]) => {
              const tot = totalOf(key, projects);
              return (
                <tr key={`total|${key}`} className="is-group">
                  <td style={{ ...TDL, fontWeight: 700 }}>{key === 'gross' ? 'Total' : ''}</td><td style={TDL}>{label}</td>
                  {tot.map((v, m) => <td key={m} style={TD}>{fmt(v)}</td>)}
                  <td style={{ ...TD, ...line }}>{fmt(tot.reduce((t, v) => t + v, 0))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Drawer({ order, drill, D, months, band, closed, baseShort, periodLabel, onClose }) {
  const [group, setGroup] = useState(drill.dimKey);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const run = runningVariance(drill.recs);
  const sel = agg(drill.recs, months);
  const kids = [...groupBy(forDim(drill.recs, group), D[group].key)].map(([k, recs]) => ({ k, name: D[group].name(k, recs[0]), a: agg(recs, months) })).sort(rowCompare(order, (c) => c.name, (c) => c.a.var));
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }} onClick={onClose} />
      <aside role="dialog" aria-modal="true" aria-label="Revenue detail" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(620px, 100%)', background: 'var(--surface)', zIndex: 1001, boxShadow: '-8px 0 30px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div><b style={{ fontSize: 16 }}>{drill.title}</b><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{periodLabel} · {baseShort}</div></div>
          <button type="button" className="btn btn-outline" onClick={onClose} autoFocus>Close</button>
        </div>
        <div style={{ padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>ACT <b>{money(sel.out)}</b></span><span>FCST <b>{money(sel.base)}</b></span>
            <b style={{ color: varColor(sel.pct, band) }}>{moneyS(sel.var)}</b><Chip pct={sel.pct} band={band} />
          </div>
          <div className="sheet-wrap">
            <table className="sheet">
              <thead><tr><th>Month</th><th style={{ textAlign: 'right' }}>Hours</th><th style={{ textAlign: 'right' }}>ACT</th><th style={{ textAlign: 'right' }}>FCST</th><th style={{ textAlign: 'right' }}>Var</th><th style={{ textAlign: 'right' }}>Running total</th><th /></tr></thead>
              <tbody>
                {run.map((r) => {
                  const a = agg(drill.recs, [r.m]);
                  return (
                    <tr key={r.m} style={{ opacity: months.includes(r.m) ? 1 : 0.6 }}>
                      <td style={TDL}>{MONTHS[r.m]} {closed.has(r.m) ? '' : <span className="badge badge-blue" style={{ fontSize: 9 }}>FCST</span>}</td>
                      <td style={TD}>{hrs(a.outH)}</td><td style={TD}>{money(a.out)}</td><td style={TD}>{money(a.base)}</td>
                      <td style={{ ...TD, color: varColor(a.pct, band) }}>{moneyS(a.var)}</td><td style={TD}>{moneyS(r.running)}</td><td style={TD}><Chip pct={a.pct} band={band} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-toolbar">
            <span className="card-title">Break down by</span>
            <select className="mt-ctl" value={group} onChange={(e) => setGroup(e.target.value)}>
              {Object.entries(D).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
            </select>
          </div>
          <div className="sheet-wrap">
            <table className="sheet">
              <thead><tr><th>{D[group].label}</th><th style={{ textAlign: 'right' }}>ACT</th><th style={{ textAlign: 'right' }}>FCST</th><th style={{ textAlign: 'right' }}>Var</th><th /></tr></thead>
              <tbody>{kids.map((c) => <tr key={c.k}><td style={TDL}>{c.name}</td><td style={TD}>{money(c.a.out)}</td><td style={TD}>{money(c.a.base)}</td><td style={{ ...TD, color: varColor(c.a.pct, band) }}>{moneyS(c.a.var)}</td><td style={TD}><Chip pct={c.a.pct} band={band} /></td></tr>)}</tbody>
            </table>
          </div>
        </div>
      </aside>
    </>
  );
}

export default RevenueTracker;
